import { useQueries } from '@tanstack/react-query';
import { inflate } from 'pako';
import { useAuth0 } from '@auth0/auth0-react';
import { type PartnerTreeNodeV2 } from '@sr/types';
import { getApiUrl } from '#lib/utils/environment';
import { useProfileV2 } from '#lib/Profile/useProfileV2';
import { useFlag, useFlagsStatus } from '#lib/hooks/useFlag';
import { WithCustomerPortalApi } from '../customerPortalApi';
import {
  combineBusinessPartnerQueries,
  type FetchBusinessPartnersResult,
} from './combineBusinessPartnerQueries';

export const useFetchBusinessPartners = WithCustomerPortalApi<
  string,
  FetchBusinessPartnersResult,
  { bpIds: string[]; allDescendants?: boolean; slim?: boolean; compress?: boolean }
>(
  (fetchFn, { bpIds, allDescendants = true, slim = true, compress = true }) => {
    const { basicProfile } = useProfileV2();
    const { isAuthenticated } = useAuth0();
    const { flagsReady } = useFlagsStatus();
    const nedreNorrPilot = useFlag('nbp-pilot-nedre-norr');

    const result = useQueries({
      combine: combineBusinessPartnerQueries,
      queries: bpIds.map((bpId) => {
        const path = getApiUrl() + `/v2/partners/${bpId}/hierarchy`;
        const url = new URL(path);

        url.searchParams.set('allDescendants', allDescendants.toString());
        url.searchParams.set('slim', slim.toString());
        url.searchParams.set('compress', compress.toString());
        url.searchParams.set('nbpPilotNedreNorr', nedreNorrPilot.toString());

        return {
          enabled: isAuthenticated && !!basicProfile && flagsReady,
          queryKey: ['business-partners', bpId, nedreNorrPilot],
          throwOnError: () => {
            //If one fails, we still want it to work
            return false;
          },
          staleTime: 8 * 60 * 60 * 1000, // 8 hours
          retry: 1, //This might slow down page load if too many retries are attempted
          queryFn: async () => {
            const base64Data = await fetchFn({
              url,
              options: {
                method: 'GET',
              },
              type: 'text',
            });

            const compressedData = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
            const uncompressedData = inflate(compressedData, { toText: true });

            const treeNode = JSON.parse(uncompressedData) as PartnerTreeNodeV2;

            return replaceNARecursive(treeNode);
          },
        };
      }),
    });

    return result;
  },
  { hookName: 'useFetchBusinessPartners' },
);

const replaceNARecursive = (node: PartnerTreeNodeV2) => {
  if (node.item.street === 'N/A') {
    node.item.street = '';
  }
  if (node.item.city === 'N/A') {
    node.item.city = '';
  }
  if (node.item.additionalDescription === 'N/A') {
    node.item.additionalDescription = '';
  }
  if (node.item.customerText === 'N/A') {
    node.item.customerText = '';
  }

  node.children.forEach((child) => replaceNARecursive(child));

  return node;
};
